/**
 * Skills API
 * Skills management endpoints
 */

import apiClient from './client';
import type { Skill, CreateSkillRequest, UpdateSkillRequest } from '@/types/skills';
import type { SkillConfig } from '@/types';

interface SkillListResponse {
  total: number;
  skills: Skill[];
}

// Validation types
export interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning';
  skill_id?: string | null;
}

export interface SkillValidationResponse {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

export interface ClassifierConfig {
  enabled?: boolean;
  skills_enabled?: boolean;
  semantic_matching_enabled?: boolean;
  semantic_similarity_threshold?: number;
  rule_confidence_threshold?: number;
  quick_response_instructions?: string;
  deep_response_instructions?: string;
  skills?: SkillConfig[];
}

export const skillsApi = {
  /**
   * List all skills
   */
  list: async (includeDisabled = false): Promise<Skill[]> => {
    const response = await apiClient.get<SkillListResponse>('/skills', {
      params: { include_disabled: includeDisabled },
    });
    return response.data.skills;
  },

  /**
   * Get a single skill by ID
   */
  get: async (skillId: string): Promise<Skill> => {
    const response = await apiClient.get(`/skills/${skillId}`);
    return response.data;
  },

  /**
   * Create a new skill
   */
  create: async (data: CreateSkillRequest): Promise<Skill> => {
    const response = await apiClient.post('/skills', data);
    return response.data;
  },

  /**
   * Update a skill
   */
  update: async (skillId: string, data: UpdateSkillRequest): Promise<Skill> => {
    const response = await apiClient.put(`/skills/${skillId}`, data);
    return response.data;
  },

  /**
   * Delete a skill
   */
  delete: async (skillId: string): Promise<void> => {
    await apiClient.delete(`/skills/${skillId}`);
  },

  /**
   * Toggle skill enabled status
   * Backend requires 'enabled' query parameter with the new state
   */
  toggle: async (skillId: string, enabled: boolean): Promise<Skill> => {
    const response = await apiClient.put(`/skills/${skillId}/toggle`, null, {
      params: { enabled },
    });
    return response.data;
  },

  /**
   * Reorder skills
   * Backend expects skill_orders array with id and display_order
   */
  reorder: async (skillIds: string[]): Promise<void> => {
    const skill_orders = skillIds.map((id, index) => ({
      id,
      display_order: index,
    }));
    await apiClient.put('/skills/reorder', { skill_orders });
  },

  /**
   * Validate skill configurations
   * Returns validation errors and warnings without saving
   */
  validate: async (skills: SkillConfig[]): Promise<SkillValidationResponse> => {
    const response = await apiClient.post<SkillValidationResponse>('/skills/validate', {
      skills,
    });
    return response.data;
  },

  /**
   * Validate full classifier configuration
   * Includes skills validation plus classifier-specific checks
   */
  validateClassifier: async (classifierConfig: ClassifierConfig): Promise<SkillValidationResponse> => {
    const response = await apiClient.post<SkillValidationResponse>('/skills/validate-classifier', {
      classifier_config: classifierConfig,
    });
    return response.data;
  },
};
